import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorInitialData,
	CreatorOptions,
	CreatorProject,
	CreatorSegment,
	CreatorState,
	CreatorWindow,
	CreatorWing,
	SegmentDivisionType,
	SegmentJoint,
	WingDivisionType,
} from '@/types/creator';
import {
	clearInvalidWingDivisions,
	getAvailableAttributes,
	loadProject,
	loadProjectTemplate,
	setAttributesAvailability,
	setComponentVariations,
	setConfigurationArticles,
	setJointDivisionType,
	setSegmentsArmorErrors,
	setWingsMosquitoErrors,
} from '@/utils/creatorUtils';
import { AttributeValue } from '@/api/AttributeValueApi';
import { SelectedMontageType } from '@/api/ProjectApi';
import { MontageType } from '@/api/ProductTypeApi';
import { CreatorActionType, UUID } from '@/types/types';
import { Color } from '@/api/ColorApi';
import DimensionsHandler from '../middlewares/DimensionsHandler';
import ActionHandler from '../middlewares/ActionHandler';
import ConfigurationArticlesQuantityCalculator from '../middlewares/ConfigurationArticlesQuantityCalculator';
import APP_CONFIG from '@/configs/appConfig';

const initialState: CreatorState = {
	projectId: null,
	productType: null,
	creatorComponents: [],
	creatorProject: {
		selectedMontageType: MontageType.INSIDE,
		configurationArticles: [],
		componentVariations: [],
		quantity: 1,
		description: '',
	},
	creatorProjectTemplate: {
		name: '',
	},
	selectedValues: {},
	availableAttributes: {},
	segmentJoints: [],
	configurationConfig: {
		project: {
			minWidth: APP_CONFIG.CONFIGURATION_DEFAULTS.PROJECT.MIN.WIDTH,
			maxWidth: APP_CONFIG.CONFIGURATION_DEFAULTS.PROJECT.MAX.WIDTH,
			minHeight: APP_CONFIG.CONFIGURATION_DEFAULTS.PROJECT.MIN.HEIGHT,
			maxHeight: APP_CONFIG.CONFIGURATION_DEFAULTS.PROJECT.MAX.HEIGHT,
			allowMosquitoNet: false,
			withArmor: false,
			maxShellsQuantityFormula: '',
		},
		segment: {
			minWidth: APP_CONFIG.CONFIGURATION_DEFAULTS.SEGMENT.MIN_WIDTH,
			maxWidth: APP_CONFIG.CONFIGURATION_DEFAULTS.SEGMENT.MAX_WIDTH,
			recommendedMinWidthRules: [
				{
					value: APP_CONFIG.CONFIGURATION_DEFAULTS.SEGMENT.MIN_WIDTH,
					withMosquitoNet: false,
				},
				{
					value: APP_CONFIG.CONFIGURATION_DEFAULTS.SEGMENT.MIN_WIDTH,
					withMosquitoNet: true,
				},
			],
			recommendedMinWidth: APP_CONFIG.CONFIGURATION_DEFAULTS.SEGMENT.MIN_WIDTH,
			recommendedMaxWidth: APP_CONFIG.CONFIGURATION_DEFAULTS.SEGMENT.MAX_WIDTH,
		},
		rollerBox: {
			availableHeights: [
				...APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.AVAILABLE_HEIGHTS,
			],
			availableDepths: [
				...APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.AVAILABLE_DEPTHS,
			],
		},
		window: {
			minHeight: APP_CONFIG.CONFIGURATION_DEFAULTS.WINDOW.MIN_HEIGHT,
		},
		wing: {
			minWidth: APP_CONFIG.CONFIGURATION_DEFAULTS.WING.MIN_WIDTH,
			maxWidth: Infinity,
			mosquitoNetMaxWidth:
				APP_CONFIG.CONFIGURATION_DEFAULTS.MOSQUITO_NET.MAX_WIDTH,
		},
		constraints: {
			[CreatorActionType.SET_SEGMENT_MAX_WIDTH]: {},
			[CreatorActionType.SET_SEGMETN_MIN_WIDTH]: {},
			[CreatorActionType.WITH_MOSQUITO_NET]: {},
			[CreatorActionType.WITH_ARMOR]: {},
		},
	},
	projectDimensions: {
		width: APP_CONFIG.CONFIGURATION_DEFAULTS.PROJECT.WIDTH,
		height: APP_CONFIG.CONFIGURATION_DEFAULTS.PROJECT.HEIGHT,
		rollerBoxHeight: APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.HEIGHT,
		rollerBoxDepth: APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.DEPTH,
		shaftDiameter: APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.SHAFT_DIAMETER,
		segmentsQuantity: 1,
	},
	segments: [],
	options: {
		isReadOnly: false,
		isTemplate: false,
		currentStep: 0,
	},
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		initialize: (
			state,
			{ payload }: PayloadAction<CreatorInitialData & { isReadOnly: boolean }>
		) => {
			const {
				isReadOnly,
				productType,
				creatorComponents,
				projectTemplate,
				project,
			} = payload;

			state.options.isReadOnly = isReadOnly;

			state.productType = productType;
			state.creatorComponents = creatorComponents;

			if (productType.montageType !== MontageType.BOTH)
				state.creatorProject.selectedMontageType = productType.montageType;

			state.selectedValues = productType.components.reduce<
				CreatorState['selectedValues']
			>((acc, component) => {
				for (const attribute of component.attributes) {
					acc[attribute.id] = null;
				}

				return acc;
			}, {});

			state.availableAttributes = getAvailableAttributes(
				creatorComponents.flatMap((comp) => comp.variations)
			);

			if (project) loadProject(state, project);
			else if (projectTemplate) loadProjectTemplate(state, projectTemplate);
			else DimensionsHandler.computeSegmentsQuantity(state);

			setAttributesAvailability(state);
			setWingsMosquitoErrors(state);
			setSegmentsArmorErrors(state);
		},
		selectAttributeValue: (
			state,
			{ payload }: PayloadAction<AttributeValue>
		) => {
			const prev = state.selectedValues[payload.attribute.id];

			state.selectedValues[payload.attribute.id] = payload;

			if (prev) ActionHandler.handleDeselect(state, prev);
			ActionHandler.handleActions(state, payload);

			if (payload.actions?.length || prev?.actions?.length)
				DimensionsHandler.computeSegmentsQuantity(state);

			setAttributesAvailability(state);
		},
		setVariationsAndArticles: (state) => {
			setComponentVariations(state);
			setConfigurationArticles(state);
		},
		setConfigurationArticleColor: (
			state,
			{
				payload,
			}: PayloadAction<{
				componentVariationArticleId: UUID;
				colorId: UUID;
				color: Color;
			}>
		) => {
			for (const configurationArticle of state.creatorProject
				.configurationArticles) {
				if (
					configurationArticle.componentVariationArticleId !==
					payload.componentVariationArticleId
				)
					continue;
				configurationArticle.colorId = payload.colorId;
				configurationArticle.color = payload.color;
				break;
			}
		},
		updateCreatorProject: (
			state,
			{ payload }: PayloadAction<Partial<CreatorProject>>
		) => {
			Object.assign(state.creatorProject, payload);
		},
		updateCreatorProjectTemplate: (
			state,
			{
				payload,
			}: PayloadAction<Partial<CreatorState['creatorProjectTemplate']>>
		) => {
			Object.assign(state.creatorProjectTemplate, payload);
		},
		updateConfigurationConfig: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState['configurationConfig']>>
		) => {
			Object.assign(state.configurationConfig, payload);
			DimensionsHandler.computeSegmentsQuantity(state);
		},
		setMontageType: (
			state,
			{ payload }: PayloadAction<SelectedMontageType>
		) => {
			state.creatorProject.selectedMontageType = payload;

			switch (payload) {
				case MontageType.INSIDE:
					state.projectDimensions.height -=
						state.projectDimensions.rollerBoxHeight;
					break;
				case MontageType.ON_WALL:
					state.projectDimensions.height +=
						state.projectDimensions.rollerBoxHeight;
					break;
			}

			DimensionsHandler.computeSegmentsQuantity(state);
		},
		setDimensions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState['projectDimensions']>>
		) => {
			if (payload.rollerBoxHeight) {
				const rollerBoxHeightDiff =
					payload.rollerBoxHeight - state.projectDimensions.rollerBoxHeight;

				state.projectDimensions.rollerBoxHeight = payload.rollerBoxHeight;
				DimensionsHandler.setAvailableRollerBoxDepths(state);

				switch (state.creatorProject.selectedMontageType) {
					case MontageType.INSIDE:
						DimensionsHandler.adjustWindowHeightsByRollerBoxHeightChange(
							state,
							rollerBoxHeightDiff
						);
						break;
					case MontageType.ON_WALL:
						state.projectDimensions.height += rollerBoxHeightDiff;
						break;
				}

				return;
			}

			Object.assign(state.projectDimensions, payload);

			if (payload.rollerBoxDepth) {
				DimensionsHandler.setAutoSelectedShaftDiameter(state);
				return;
			}

			DimensionsHandler.computeSegmentsQuantity(state);
		},
		setSegmentsQuantity: (state, { payload }: PayloadAction<number>) => {
			const { min: minSegments, max: maxSegments } =
				DimensionsHandler.calculateMinMaxSegments(
					state.projectDimensions.width,
					state.configurationConfig
				);

			if (payload < minSegments || payload > maxSegments) return;
			DimensionsHandler.setSegmentsQuantity(state, payload);
		},
		setSegmentWidth: (
			state,
			{ payload }: PayloadAction<{ id: UUID; width: number }>
		) => {
			for (const segment of state.segments) {
				if (segment.id !== payload.id) continue;

				segment.width = payload.width;
				DimensionsHandler.handleSegmentWidthChange(segment, state);
				break;
			}

			setWingsMosquitoErrors(state);
			setSegmentsArmorErrors(state);
		},
		updateSegmentJoint: (
			state,
			{ payload }: PayloadAction<{ idx: number; data: Partial<SegmentJoint> }>
		) => {
			const joint = state.segmentJoints[payload.idx];
			if (!joint) return;

			if (payload.data.divisionType)
				setJointDivisionType(joint, payload.data.divisionType);

			if (
				payload.data.driverJunction !== undefined &&
				joint.divisionType === SegmentDivisionType.THIN
			)
				joint.driverJunction = payload.data.driverJunction;

			setSegmentsArmorErrors(state);
		},
		updateSegment: (
			state,
			{ payload }: PayloadAction<{ id: UUID; data: Partial<CreatorSegment> }>
		) => {
			for (const segment of state.segments) {
				if (segment.id !== payload.id) continue;
				Object.assign(segment, payload.data);
				break;
			}
		},
		updateWindow: (
			state,
			{ payload }: PayloadAction<Partial<CreatorWindow>>
		) => {
			for (let index = 0; index < state.segments.length; index++) {
				const segment = state.segments[index];
				if (segment.window.id !== payload.id) continue;

				const prevSegment = state.segments[index - 1],
					nextSegment = state.segments[index + 1];

				const nextJoint = state.segmentJoints[index],
					prevJoint = state.segmentJoints[index - 1];

				Object.assign(segment.window, payload);

				if (
					prevJoint &&
					prevSegment &&
					segment.window.height !== prevSegment.window.height
				)
					setJointDivisionType(prevJoint, SegmentDivisionType.WIDE);

				if (
					nextJoint &&
					nextSegment &&
					segment.window.height !== nextSegment.window.height
				)
					setJointDivisionType(nextJoint, SegmentDivisionType.WIDE);

				break;
			}

			if (state.creatorProject.selectedMontageType === MontageType.ON_WALL)
				state.projectDimensions.height = Math.max(
					...state.segments.map((s) => s.window.height)
				);

			setSegmentsArmorErrors(state);
		},
		updateWing: (
			state,
			{
				payload,
			}: PayloadAction<{ segmentId: UUID; wing: Partial<CreatorWing> }>
		) => {
			for (const segment of state.segments) {
				if (segment.id !== payload.segmentId) continue;

				const wing = segment.window.wings.find((w) => w.id === payload.wing.id);
				if (!wing) return;
				Object.assign(wing, payload.wing);
				if (payload.wing.withMosquitoNet === false)
					clearInvalidWingDivisions(segment.window);
				break;
			}

			setWingsMosquitoErrors(state);
			setSegmentsArmorErrors(state);
		},
		setWingWidth: (
			state,
			{
				payload,
			}: PayloadAction<{
				segmentId: UUID;
				data: {
					id: UUID;
					width: number;
				};
			}>
		) => {
			const { id, width } = payload.data;

			for (const segment of state.segments) {
				if (segment.id !== payload.segmentId) continue;

				const wing = segment.window.wings.find((w) => w.id === id);
				if (!wing) return;
				DimensionsHandler.setSegmentWingsWidth(
					segment,
					wing,
					width,
					state.configurationConfig.wing.minWidth
				);
				break;
			}

			setWingsMosquitoErrors(state);
			setSegmentsArmorErrors(state);
		},
		setWingDivisionType: (
			state,
			{
				payload,
			}: PayloadAction<{
				segmentId: UUID;
				idx: number;
				divisionType: WingDivisionType;
			}>
		) => {
			for (const segment of state.segments) {
				if (segment.id !== payload.segmentId) continue;
				segment.window.wingDivisions[payload.idx] = payload.divisionType;
				clearInvalidWingDivisions(segment.window);
				break;
			}

			setWingsMosquitoErrors(state);
			setSegmentsArmorErrors(state);
		},
		setWingsQuantity: (
			state,
			{ payload }: PayloadAction<{ segmentId: UUID; quantity: number }>
		) => {
			for (const segment of state.segments) {
				if (segment.id !== payload.segmentId) continue;

				segment.window.wings = [];

				const calculatedWidth = Math.round(segment.width / payload.quantity);

				for (let i = 0; i < payload.quantity; i++) {
					segment.window.wings.push({
						id: crypto.randomUUID(),
						isMosquitoError: false,
						width: calculatedWidth,
						withMosquitoNet: false,
					});
				}

				DimensionsHandler.computeWingsDivisions(state, payload.segmentId);
				break;
			}

			setWingsMosquitoErrors(state);
			setSegmentsArmorErrors(state);
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		setConfigurationArticlesQuantity: (state) => {
			ConfigurationArticlesQuantityCalculator.setConfigurationArticlesQuantity(
				state
			);
		},
		setRollerBoxConfiguration: (state) => {
			DimensionsHandler.setAvailableRollerBoxHeights(state);
			DimensionsHandler.setAvailableRollerBoxDepths(state);
			DimensionsHandler.adjustWindowHeights(state);
		},
		setMaxProjectHeight: (state) => {
			DimensionsHandler.setMaxProjectHeight(state);

			if (
				state.projectDimensions.height >
				state.configurationConfig.project.maxHeight
			) {
				state.projectDimensions.height =
					state.configurationConfig.project.maxHeight;
				DimensionsHandler.computeSegmentsQuantity(state);
			}
		},
		navigateCreatorStep: (
			state,
			{ payload }: PayloadAction<'prev' | 'next'>
		) => {
			payload === 'prev'
				? state.options.currentStep--
				: state.options.currentStep++;
		},
		resetCreator: (state) => {
			Object.assign(state, initialState);
		},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
